import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import { todayISO } from '../../utils/dates.js'
import MyPages from './MyPages.jsx'
import MyTrackers from './MyTrackers.jsx'
import MyGoals from './MyGoals.jsx'
import SectionWelcome from '../../components/SectionWelcome.jsx'
import '../../styles/my-space.css'

const TABS = [
  { id: 'overview',  label: 'Overview' },
  { id: 'pages',     label: 'My Pages' },
  { id: 'trackers',  label: 'My Trackers' },
  { id: 'goals',     label: 'My Goals' },
]

function formatRelative(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T12:00:00' : ''))
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000)
  if (diff === 0) return 'today'
  if (diff === 1) return 'yesterday'
  if (diff < 7)  return `${diff} days ago`
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

// ── Overview ──────────────────────────────────────────────────────────────────

function Overview({ layoutClass, onNavigate }) {
  const [pages, setPages]     = useState([])
  const [trackers, setTrackers] = useState([])
  const [entries, setEntries] = useState([])
  const [goals, setGoals]     = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api.getMyPages(),
      api.getMyTrackers(),
      api.getGoals(),
    ]).then(([p, t, g]) => {
      setPages(p || [])
      setTrackers(t || [])
      setGoals(g || [])
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (!trackers.length) return
    const today = todayISO()
    const sevenAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10)
    Promise.all(
      trackers.map(t => api.getTrackerEntries(t.id, sevenAgo, today))
    ).then(results => {
      const flat = results.flatMap((r, i) =>
        (r || []).map(e => ({ ...e, trackerTitle: trackers[i].title, trackerIcon: trackers[i].icon }))
      )
      flat.sort((a, b) => b.date.localeCompare(a.date))
      setEntries(flat)
    })
  }, [trackers])

  if (loading) return null

  const pinnedPages     = pages.filter(p => p.pinned)
  const activeTrackers  = new Set(entries.map(e => e.tracker_id)).size
  const inProgressGoals = goals.filter(g => g.status === 'active')
  const recentEntries   = entries.slice(0, 5)

  function parseValue(raw) {
    if (typeof raw === 'object' && raw !== null) return raw
    try { return JSON.parse(raw || '{}') } catch { return {} }
  }

  function entryLabel(entry) {
    const v = parseValue(entry.value)
    if (v.done)  return 'Done ✓'
    if (v.value != null) return String(v.value)
    const keys = Object.keys(v)
    if (keys.length) return `${keys[0]}: ${v[keys[0]]}`
    return 'Logged'
  }

  return (
    <div>
      {/* Stats */}
      <div className="ms-stat-row">
        <div className={`ms-card ms-stat-card`}>
          <div className="stat-num">{pages.length}</div>
          <div className="stat-lbl">Pages</div>
        </div>
        <div className={`ms-card ms-stat-card`}>
          <div className="stat-num">{activeTrackers}</div>
          <div className="stat-lbl">Active trackers</div>
        </div>
        <div className={`ms-card ms-stat-card`}>
          <div className="stat-num">{inProgressGoals.length}</div>
          <div className="stat-lbl">Goals in progress</div>
        </div>
      </div>

      {/* Pinned pages */}
      <div className="ms-section-label">📌 Pinned pages</div>
      {pinnedPages.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--stone)', fontStyle: 'italic', marginBottom: '1.5rem' }}>
          Pin a page to see it here 📌
        </p>
      ) : (
        <div className="ms-pinned-row" style={{ marginBottom: '1.5rem' }}>
          {pinnedPages.map(p => (
            <div
              key={p.id}
              className="ms-pinned-card"
              onClick={() => onNavigate('pages', p.id)}
            >
              <div
                className="pc-cover"
                style={p.cover_image
                  ? { backgroundImage: `url(${p.cover_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                  : { background: p.cover_color || '#f5f0e8' }
                }
              />
              <div className="pc-body">
                <div className="pc-title">{p.icon || '📄'} {p.title}</div>
                <div className="pc-date">{formatRelative(p.updated_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent tracker activity */}
      <div className="ms-section-label">📊 Recent activity</div>
      {recentEntries.length === 0 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--stone)', fontStyle: 'italic', marginBottom: '1.5rem' }}>
          Your tracker activity will appear here 🌿
        </p>
      ) : (
        <div className="ms-feed" style={{ marginBottom: '1.5rem' }}>
          {recentEntries.map((e, i) => (
            <div key={i} className="ms-feed-item">
              <span className="ms-feed-icon">{e.trackerIcon || '📊'}</span>
              <span>{e.trackerTitle} — {entryLabel(e)}</span>
              <span className="ms-feed-meta">{formatRelative(e.date)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Goals snapshot */}
      <div className="ms-section-label">🎯 Goals</div>
      {inProgressGoals.length === 0 ? (
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ fontSize: '0.85rem', color: 'var(--stone)', fontStyle: 'italic', marginBottom: '0.75rem' }}>
            No goals in progress yet — add one in My Goals.
          </p>
          <button className="ghost-btn" style={{ fontSize: '0.82rem' }} onClick={() => onNavigate('goals')}>
            Add a goal →
          </button>
        </div>
      ) : (
        <div className={`ms-card`} style={{ marginBottom: '1.5rem' }}>
          {inProgressGoals.slice(0, 3).map(g => (
            <div key={g.id} className="ms-goal-item">
              <div className="ms-goal-title">{g.title}</div>
              <div className="ms-goal-bar-wrap">
                <div className="ms-goal-bar" style={{ width: `${g.progress || 0}%` }} />
              </div>
              <div className="ms-goal-pct">{g.progress || 0}%</div>
            </div>
          ))}
          <button className="ghost-btn" style={{ fontSize: '0.82rem', marginTop: '0.5rem' }} onClick={() => onNavigate('goals')}>
            View all goals →
          </button>
        </div>
      )}
    </div>
  )
}

// ── Main MySpace shell ────────────────────────────────────────────────────────

export default function MySpace() {
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search)
    const t = p.get('tab')
    return TABS.find(x => x.id === t) ? t : 'overview'
  })
  const [layout, setLayout] = useState(
    () => localStorage.getItem('mySpaceLayout') || 'cosy'
  )
  const [openPageId, setOpenPageId] = useState(null)
  const [goalsVisited, setGoalsVisited] = useState(() => {
    const p = new URLSearchParams(window.location.search)
    return p.get('tab') === 'goals'
  })

  function goTab(id, pageId = null) {
    setTab(id)
    if (id === 'goals') setGoalsVisited(true)
    setOpenPageId(pageId || null)
    history.replaceState(null, '', `?tab=${id}`)
  }

  function handleLayoutToggle() {
    const next = layout === 'cosy' ? 'minimal' : 'cosy'
    setLayout(next)
    localStorage.setItem('mySpaceLayout', next)
  }

  const layoutClass = `my-space--${layout}`

  return (
    <div className={`page active ${layoutClass}`} id="page-my-space">
      <div className="page-inner">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
          <div className="hero" style={{ marginBottom: 0 }}>
            <p className="hero-eyebrow">My Space</p>
            <h1>My <em>space</em> ✦</h1>
            <hr className="hero-rule" />
            <SectionWelcome text="Your personal corner of the app. Pages, trackers, and goals — shaped entirely around you." />
          </div>
          <button
            className={`layout-toggle${layout === 'cosy' ? ' active' : ''}`}
            onClick={handleLayoutToggle}
            style={{ marginTop: '0.5rem', flexShrink: 0 }}
          >
            {layout === 'cosy' ? '🌿 Cosy' : '□ Minimal'}
          </button>
        </div>

        {/* Tabs */}
        <div className="cooking-tabs" style={{ marginBottom: '1.5rem' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              className={`cooking-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => goTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'overview' && (
          <Overview layoutClass={layoutClass} onNavigate={goTab} />
        )}
        {tab === 'pages' && (
          <MyPages layoutClass={layoutClass} initialOpenPageId={openPageId} />
        )}
        {tab === 'trackers' && (
          <MyTrackers layoutClass={layoutClass} />
        )}
        {goalsVisited && (
          <div style={{ display: tab === 'goals' ? 'block' : 'none' }}>
            <MyGoals layoutClass={layoutClass} />
          </div>
        )}

      </div>
    </div>
  )
}
